import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { facturasCompraService } from '../services/facturasCompraAPI';
import type { EditarFacturaCompraRequest } from '../services/facturasCompraAPI';
import type {
  FiltrosFacturaCompra,
  CrearFacturaCompraRequest,
  ValidarDuplicadoRequest,
} from '../types/facturas';

// Query keys para React Query
export const facturasCompraKeys = {
  all: ['facturas-compra'] as const,
  lists: () => [...facturasCompraKeys.all, 'list'] as const,
  list: (params?: FiltrosFacturaCompra) =>
    [...facturasCompraKeys.lists(), params] as const,
  details: () => [...facturasCompraKeys.all, 'detail'] as const,
  detail: (id: number) => [...facturasCompraKeys.details(), id] as const,
  estadisticas: (params?: { fecha_desde?: string; fecha_hasta?: string }) =>
    [...facturasCompraKeys.all, 'estadisticas', params] as const,
};

/**
 * Hook para listar facturas de compra con filtros
 */
export const useFacturasCompra = (filtros?: FiltrosFacturaCompra) => {
  return useQuery({
    queryKey: facturasCompraKeys.list(filtros),
    queryFn: () => facturasCompraService.listar(filtros),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obtener una factura específica
 */
export const useFacturaCompra = (id: number, enabled = true) => {
  return useQuery({
    queryKey: facturasCompraKeys.detail(id),
    queryFn: () => facturasCompraService.obtenerPorId(id),
    enabled: enabled && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

/**
 * Hook para validar facturas duplicadas
 */
export const useValidarDuplicado = () => {
  return useMutation({
    mutationFn: (data: ValidarDuplicadoRequest) =>
      facturasCompraService.validarDuplicado(data),
  });
};

/**
 * Hook para crear factura de compra
 */
export const useCrearFacturaCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CrearFacturaCompraRequest) =>
      facturasCompraService.crear(data),
    onSuccess: () => {
      // Invalidar todas las queries de facturas
      queryClient.invalidateQueries({
        queryKey: facturasCompraKeys.all,
        refetchType: 'all',
      });

      // También invalidar ingredientes porque se actualizó stock
      queryClient.invalidateQueries({
        queryKey: ['ingredientes'],
        refetchType: 'all',
      });
    },
  });
};

/**
 * Hook para editar factura (solo borradores)
 */
export const useEditarFacturaCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditarFacturaCompraRequest }) =>
      facturasCompraService.editar(id, data),
    onSuccess: (_result, variables) => {
      // Invalidar detalle especifico y listas
      queryClient.invalidateQueries({
        queryKey: facturasCompraKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: facturasCompraKeys.all,
        refetchType: 'all',
      });
      // Tambien invalidar ingredientes porque el stock puede cambiar
      queryClient.invalidateQueries({
        queryKey: ['ingredientes'],
        refetchType: 'all',
      });
    },
  });
};

/**
 * Hook para anular factura
 */
export const useAnularFactura = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => facturasCompraService.anular(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: facturasCompraKeys.all,
        refetchType: 'all',
      });
    },
  });
};

/**
 * Hook para procesar OCR
 */
export const useProcesarOCR = () => {
  return useMutation({
    mutationFn: (archivo: File) => facturasCompraService.procesarOCR(archivo),
  });
};

/**
 * Hook para obtener estadísticas de facturas
 */
export const useEstadisticasFacturas = (
  fechaDesde?: string,
  fechaHasta?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: facturasCompraKeys.estadisticas({
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    }),
    queryFn: () =>
      facturasCompraService.obtenerEstadisticas(fechaDesde, fechaHasta),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};
